"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BackgroundJobService = exports.isProgressResultPayload = exports.BackgroundJobStatus = void 0;
const minimatch_1 = __importDefault(require("minimatch"));
const feature_1 = require("../log/feature");
const BACKGROUND_JOB_CACHE_EXPIRATION_SECONDS = 60 * 60; // 1 hour
const CURRENT_JOBS_CACHE_KEY = 'bg-jobs';
function backgroundJobCacheKey(identifier) {
    return `bg-job-${identifier}`;
}
var BackgroundJobStatus;
(function (BackgroundJobStatus) {
    BackgroundJobStatus["Pending"] = "PENDING";
    BackgroundJobStatus["Processing"] = "PROCESSING";
    BackgroundJobStatus["Error"] = "ERROR";
    BackgroundJobStatus["Success"] = "SUCCESS";
})(BackgroundJobStatus || (exports.BackgroundJobStatus = BackgroundJobStatus = {}));
function isProgressResultPayload(payload) {
    const progressPayload = payload;
    return (progressPayload.total !== undefined &&
        progressPayload.total !== null &&
        !isNaN(parseFloat(progressPayload.total.toString())) &&
        progressPayload.current !== undefined &&
        progressPayload.current !== null &&
        !isNaN(parseFloat(progressPayload.current.toString())));
}
exports.isProgressResultPayload = isProgressResultPayload;
const statusMap = [
    BackgroundJobStatus.Pending,
    BackgroundJobStatus.Processing,
    BackgroundJobStatus.Success,
    BackgroundJobStatus.Error,
];
function getMaximumStatus(statuses) {
    if (!statuses.length)
        throw new Error('You must pass at least one status.');
    const maxIndex = statuses.slice(1).reduce((maxIndex, status) => {
        const index = statusMap.indexOf(status);
        return index > maxIndex ? index : maxIndex;
    }, statusMap.indexOf(statuses[0]));
    return statusMap[maxIndex];
}
function mergeResultPayloads(payloads) {
    return payloads.slice(1).reduce((acc, payload) => {
        if (acc && payload && isProgressResultPayload(acc) && isProgressResultPayload(payload)) {
            return Object.assign(Object.assign(Object.assign({}, acc), payload), { current: Math.max(payload.current, acc.current), total: Math.max(payload.total, acc.total) });
        }
        else
            return Object.assign(Object.assign({}, acc), payload);
    }, payloads[0]);
}
function getExpectedErrorDetails(err) {
    if (err && typeof err === 'object' && err.extensions && err.extensions.code && err.message) {
        const _a = err.extensions, { code } = _a, rest = __rest(_a, ["code"]);
        return {
            code,
            message: err.message,
            extensions: rest,
        };
    }
    else
        return undefined;
}
class BackgroundJobService {
    constructor(root) {
        this._root = root;
    }
    _getDefaultQueueIdentifier() {
        return __awaiter(this, void 0, void 0, function* () {
            const defaultIdentifier = yield this._root.deps.hookManager.mutateHook('feature-server:background-job-default-queue-identifier', undefined, undefined);
            if (!defaultIdentifier) {
                throw new Error('No default background job queue identifier is registered.');
            }
            return defaultIdentifier;
        });
    }
    /**
     * Schedules a new background job
     * @param identifier a unique identifier for the job
     * @param payload the payload to pass to the handler
     * @returns a promise resolving to the tracking identifier if one is enabled
     */
    scheduleJob(identifier, payload) {
        return __awaiter(this, void 0, void 0, function* () {
            const jobResource = this._root.deps.resources.getResource('BackgroundJob', identifier);
            if (!jobResource) {
                throw new Error(`Invalid background job identifier: ${identifier}`);
            }
            if (jobResource.getTrackingIdentifier && jobResource.atomic) {
                const taskIdentifier = jobResource.getTrackingIdentifier(payload);
                (0, feature_1.debug)('checking for existing background jobs with identifier %s', taskIdentifier);
                const existing = yield this.getJobStatus(identifier, taskIdentifier);
                if ((existing === null || existing === void 0 ? void 0 : existing.status) === BackgroundJobStatus.Processing ||
                    (existing === null || existing === void 0 ? void 0 : existing.status) === BackgroundJobStatus.Pending) {
                    (0, feature_1.debug)('job already exists; doing nothing');
                    return taskIdentifier;
                }
            }
            if (jobResource.getTrackingIdentifier) {
                yield this.updateJobStatus(identifier, jobResource.getTrackingIdentifier(payload), payload, BackgroundJobStatus.Pending);
            }
            // TODO: Replace me with a more centralized way of determining if we are in a local environment.
            //   Right now, this only works on servers, but that's the only place we use the background job service.
            if (!process.env.LAMBDA_TASK_ROOT) {
                (0, feature_1.debug)('executing IMMEDIATELY background job %s with payload %O', identifier, payload);
                if (process.env.NODE_ENV === 'test') {
                    yield this.handleJobWithoutQueue(jobResource, identifier, payload);
                }
                else {
                    (0, feature_1.debug)('...but waiting 2 seconds to simulate Lambda start times...');
                    new Promise(resolve => setTimeout(resolve, 2000))
                        .then(() => {
                        return this.handleJobWithoutQueue(jobResource, identifier, payload);
                    })
                        .catch((err) => {
                        (0, feature_1.debug)('error when processing background job immediately');
                        (0, feature_1.debug)(err);
                    });
                }
            }
            else {
                const queueIdentifier = jobResource.queueIdentifier || (yield this._getDefaultQueueIdentifier());
                const boundQueue = this._root.deps.services.queue(queueIdentifier.store, queueIdentifier.identifier);
                (0, feature_1.debug)('scheduling background job %s with payload %O', identifier, payload);
                yield boundQueue.sendMessage({
                    identifier,
                    payload,
                });
            }
            if (jobResource.getTrackingIdentifier) {
                return jobResource.getTrackingIdentifier(payload);
            }
            else
                return null;
        });
    }
    /**
     * Gets the current status of all pending background tasks matching a specific pattern.
     * @param pattern the glob pattern to use to match background jobs
     * @returns the current status of all matching jobs
     */
    getAllJobStatus(pattern) {
        return __awaiter(this, void 0, void 0, function* () {
            const keys = (yield this._root.deps.services.cache().setMembers(CURRENT_JOBS_CACHE_KEY)) || [];
            const matchingKeys = keys.filter(minimatch_1.default.filter(backgroundJobCacheKey(pattern)));
            const result = yield this._root.deps.services
                .cache()
                .batchGetKeys(matchingKeys);
            return Object.keys(result).map(key => (Object.assign({ key }, result[key])));
        });
    }
    /**
     * Gets the current status of a pending background task.
     * @param jobIdentifier the identifier of the background job to check the status from
     * @param identifier the identifier of the individual task to check
     * @returns the current status of the job, if it's available
     */
    getJobStatus(jobIdentifier, identifier) {
        return __awaiter(this, void 0, void 0, function* () {
            const jobResource = this._root.deps.resources.getResource('BackgroundJob', jobIdentifier);
            if (!jobResource) {
                throw new Error(`Invalid background job identifier: ${jobIdentifier}`);
            }
            if (!jobResource.getTrackingIdentifier) {
                throw new Error(`Background job tracking is not enabled for ${jobIdentifier}`);
            }
            const key = backgroundJobCacheKey(identifier);
            const result = yield this._root.deps.services
                .cache()
                .get(key);
            return result ? Object.assign(Object.assign({}, result), { key }) : null;
        });
    }
    /**
     * scheduleJob returns a tracking identifier for a created job, but the ID for the job
     * itself is different. This method converts a tracking identifier to a job ID.
     */
    getBackgroundJobId(trackingIdentifier) {
        return backgroundJobCacheKey(trackingIdentifier);
    }
    updateJobStatus(jobIdentifier, identifier, payload, status, resultPayload, errorDetails) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, feature_1.debug)('updating status for %s to %s', identifier, status);
            (0, feature_1.debug)('...with resultPayload %O', resultPayload);
            // FIXME This is still prone to race conditions if it is updated VERY quickly.
            const existing = status !== BackgroundJobStatus.Pending
                ? yield this.getJobStatus(jobIdentifier, identifier)
                : undefined;
            const cacheKey = backgroundJobCacheKey(identifier);
            yield this._root.deps.services.cache().put(cacheKey, {
                identifier: jobIdentifier,
                payload: payload || (existing === null || existing === void 0 ? void 0 : existing.payload),
                resultPayload: mergeResultPayloads([resultPayload, existing === null || existing === void 0 ? void 0 : existing.resultPayload]),
                errorDetails,
                status: (existing === null || existing === void 0 ? void 0 : existing.status) ? getMaximumStatus([status, existing.status]) : status,
            }, BACKGROUND_JOB_CACHE_EXPIRATION_SECONDS);
            yield this._root.deps.services
                .cache()
                .setAdd(CURRENT_JOBS_CACHE_KEY, [cacheKey], BACKGROUND_JOB_CACHE_EXPIRATION_SECONDS);
        });
    }
    handleJobWithoutQueue(resource, identifier, payload) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (resource.getTrackingIdentifier) {
                    yield this.updateJobStatus(identifier, resource.getTrackingIdentifier(payload), payload, BackgroundJobStatus.Processing);
                }
                this._root.deps.services.error.setContext('backgroundJob', payload);
                const resultPayload = yield resource.handle(payload);
                if (resource.getTrackingIdentifier) {
                    yield this.updateJobStatus(identifier, resource.getTrackingIdentifier(payload), payload, BackgroundJobStatus.Success, resultPayload !== null && resultPayload !== void 0 ? resultPayload : undefined);
                }
            }
            catch (err) {
                (0, feature_1.warn)('error processing background job: %O', err);
                this._root.deps.services.error.reportError(err);
                if (resource.getTrackingIdentifier) {
                    yield this.updateJobStatus(identifier, resource.getTrackingIdentifier(payload), payload, BackgroundJobStatus.Error, undefined, getExpectedErrorDetails(err));
                }
            }
        });
    }
    handleJob(receivedMessageIdentifier, { identifier, payload }) {
        return __awaiter(this, void 0, void 0, function* () {
            const jobResource = this._root.deps.resources.getResource('BackgroundJob', identifier);
            if (!jobResource) {
                throw new Error(`Invalid background job identifier: ${identifier}`);
            }
            const queueIdentifier = jobResource.queueIdentifier || (yield this._getDefaultQueueIdentifier());
            const boundQueue = this._root.deps.services.queue(queueIdentifier.store, queueIdentifier.identifier);
            try {
                (0, feature_1.debug)('processing background job %s with payload %O', identifier, payload);
                yield this.handleJobWithoutQueue(jobResource, identifier, payload);
                (0, feature_1.debug)('processing complete; removing message');
                yield boundQueue.deleteMessage(receivedMessageIdentifier);
            }
            catch (err) {
                (0, feature_1.warn)('error processing background job: %O', err);
                this._root.deps.services.error.reportError(err);
            }
        });
    }
}
exports.BackgroundJobService = BackgroundJobService;
//# sourceMappingURL=BackgroundJobService.js.map